import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  NgbCalendar,
  NgbDate,
  NgbDatepicker,
  NgbDatepickerModule,
  NgbDateStruct,
  NgbInputDatepickerConfig,
} from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, debounceTime, filter } from 'rxjs';
import {
  PRICE_LOADER_CONFIG,
  QUERY_PARAM_ATTRIBUTES,
  RATE_CALENDAR_SETTINGS,
  URL_PATHS,
  checkRecaptchaConfig,
} from '../../../../common/configs/common.config';
import { CalendarService } from '../../../../common/services/calendar.service';
import { CustomNGBDatepickerI18n } from '../../../../common/services/custom-ngb-datepicker-i18n.service';
import { ModalService } from '../../../../common/services/modal.service';
import { StoreService } from '../../../../store/store.service';
import { CommonService } from '../../../../common/services/common.service';
import { FADE, GROW } from '../../../../common/animations/animations';
import { CalendarDayComponent } from '../calendar-day/calendar-day.component';
import { CalendarSummaryComponent } from '../calendar-summary/calendar-summary.component';
import { CalendarIndicationsComponent } from '../calendar-indications/calendar-indications.component';
import { ButtonGroupComponent, ButtonItem } from '../../../../common/components/button-group/button-group.component';
import { LayoutService } from '../../../../common/services/layout.service';
import { RouterService } from '../../../../common/services/router.service';
import { DateTime } from 'luxon';
import { RecaptchaService } from '../../../../common/services/recaptcha.service';
import { isEmpty, isNull } from 'lodash';

@Component({
  standalone: true,
  imports: [
    FormsModule,
    NgbDatepickerModule,
    CommonModule,
    CalendarDayComponent,
    CalendarIndicationsComponent,
    CalendarSummaryComponent,
    ButtonGroupComponent,
  ],
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss',
  animations: [FADE, GROW],
  host: {
    '[@grow]': 'true',
  },
})
export class CalendarComponent {
  // Optional Inputs
  public readonly hideSummary = input<boolean>(false);
  public readonly isModal = input<boolean>(false);
  public readonly isSidePanel = input<boolean>(false);
  public readonly isCalendarSidepanel = input<boolean>(false);

  // Outputs
  public readonly closeModalEvent = output<void>();

  // Properties
  public priceLoaderConfig: typeof PRICE_LOADER_CONFIG = PRICE_LOADER_CONFIG;
  public selectedDate!: NgbDateStruct;
  public hoveredDate: NgbDate | null = null;
  public fromDate!: NgbDate | null;
  public toDate!: NgbDate | null;

  // Signals
  public readonly minDate = this.calendarSvc.minDate;
  public readonly maxDate = this.calendarSvc.maxDate;
  public readonly loadedMonths = this.calendarSvc.loadedMonths;
  public readonly dayRates = this.calendarSvc.dayRates;
  public readonly oldDayRates = this.calendarSvc.oldDayRates;
  public readonly monthsLoading = this.calendarSvc.monthsLoading;
  public readonly isDayRatesLoading = this.calendarSvc.isDayRatesLoading;
  public readonly isStayDayRatesLoading = this.calendarSvc.isStayDayRatesLoading;
  public readonly errorMsg = signal<string>('');
  public readonly isMultiProp = signal<boolean>(false);
  public readonly showCalendar = signal<boolean>(true);
  public readonly isSelectRoomInProgress = signal<boolean>(false);
  public readonly calendarContainerWidth = signal<number | null>(null);
  private calendarContainerObserver?: ResizeObserver;
  public readonly noOfMonthsToDisplay = signal<number>(2);
  public navigateToDate$: BehaviorSubject<NgbDate | null> = new BehaviorSubject<NgbDate | null>(null);

  // Computed Properties
  public readonly calendarStartOnSunday = computed(
    () => this.storeSvc.userStore.propertyInfo().calendarStartDay !== 'Monday',
  );
  public readonly btnGroupOptions = computed<ButtonItem[]>(() => {
    const localeObj = this.storeSvc.userStore.localeObj();
    const rateQuoteData = this.calendarSvc.stayDatesData();
    const isStayDateSelectionInProgress = this.calendarSvc.isStayDateSelectionInProgress();
    const isStayDayRatesLoading = this.calendarSvc.isStayDayRatesLoading();
    const isAltPropertyAvailable = this.calendarSvc.isAltPropertyAvailable();

    return [
      {
        label: localeObj?.tf_1_Calendar_rateCalender_btn_cancel,
        variant: 'secondary',
        id: 'back-to-offers',
        value: 'CANCEL',
      },
      {
        label: localeObj?.tf_1_Calendar_rateCalender_btn_save,
        variant: 'primary',
        id: 'proceed-to-book-and-join',
        value: 'SAVE',
        isDisabled:
          (!rateQuoteData?.available && !isAltPropertyAvailable) ||
          isStayDateSelectionInProgress ||
          isStayDayRatesLoading,
      },
    ];
  });
  public readonly isMobile = computed(() => {
    return this.storeSvc.res()?.mobile ?? false;
  });
  public readonly isMobileLarge = computed(() => {
    return this.storeSvc.res()?.mobileLarge ?? false;
  });

  // View Children
  public readonly ngbDatepickerRef = viewChild('dp', { read: NgbDatepicker });
  public readonly calendarContainerRef = viewChild<ElementRef<HTMLDivElement>>('calendarContainer');
  public readonly footerEle = viewChild('footer', { read: ElementRef });

  constructor(
    public readonly ngbConfigSvc: NgbInputDatepickerConfig,
    public readonly ngbCustomI18nSvc: CustomNGBDatepickerI18n,
    public readonly calendarSvc: CalendarService,
    public readonly ngbCalSvc: NgbCalendar,
    public readonly layoutSvc: LayoutService,
    public readonly storeSvc: StoreService,
    public readonly commonSvc: CommonService,
    public readonly modalSvc: ModalService,
    public readonly destroyRef: DestroyRef,
    public readonly breakpointObserverSvc: BreakpointObserver,
    private readonly recaptchaSvc: RecaptchaService,
    public readonly routerSvc: RouterService,
  ) {
    // Execute Calendar scripts when portal scripts are loaded
    effect(() => {
      if (this.layoutSvc.isPortalScriptsLoaded) {
        this.layoutSvc.executePageScripts('Calendar');
      }
    });

    effect(() => {
      // Clear the calendar data and unload scripts when the component is destroyed
      this.destroyRef.onDestroy(() => {
        this.calendarSvc.dayRates.clear();
        this.calendarSvc.oldDayRates.clear();
        this.calendarSvc.loadedMonths.clear();
        this.calendarSvc.monthsLoading.clear();
        this.calendarSvc.isDayRatesLoading.set(false);
        this.calendarSvc.isStayDayRatesLoading.set(false);
        this.calendarSvc.stayDayRatesLoading.clear();
        this.calendarSvc.stayDayRates.clear();
        this.calendarSvc.isStayDateSelectionInProgress.set(false);
        // Unload Calendar scripts
        this.layoutSvc.unloadPageScripts('Calendar');
        this.calendarContainerObserver?.disconnect();
      });
    });

    // Watch for currency changes and refetch rates with new currency
    // Only currency changes should trigger this effect
    effect(() => {
      const currency = this.storeSvc.userStore.currency();

      // Only refetch if currency code exists and calendar has already loaded data
      if (currency?.code) {
        this.calendarSvc.refreshCalendar();
      }
    });

    // Watch for preferences changes and refetch rates with new currency
    this.storeSvc.preferencesUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ isCurrencyChanged, isLanguageChanged }) => {
        if ((!isLanguageChanged && isCurrencyChanged) || (isLanguageChanged && isCurrencyChanged)) {
          this.calendarSvc.refreshCalendar();
        }

        if (isLanguageChanged && !isCurrencyChanged) {
          this.refreshStayDayRates();
        }
      });

    // Watch for day use rooms enabled changes and
    // if the stay dates are the same, then update the selected to date to have atleast 1 day difference
    // between the from and to date
    effect(() => {
      const isDayUseRoomsEnabled = this.storeSvc.appStore.isDayUseRoomsEnabled();
      if (!isDayUseRoomsEnabled && !!this.fromDate && !!this.toDate && this.fromDate.equals(this.toDate)) {
        this.selectDates(this.fromDate as NgbDateStruct, undefined, 1);
      }
    });

    // Updates the calendar months to month having the selected date
    this.navigateToDate$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((date) => {
      if (!this.ngbDatepickerRef()) {
        setTimeout(() => {
          this.navigateToDate$.next(date);
        }, 1000);
        return;
      }

      if (date && this.ngbDatepickerRef()) {
        this.ngbDatepickerRef()?.navigateTo(date);
        this.navigateToDate$.next(null);
      }
    });

    // break points observer
    this.breakpointObserverSvc
      .observe(['(max-width: 1023px)', '(min-width: 768px) and (max-width: 1920px)'])
      ?.subscribe((state: BreakpointState) => {
        const isTabletScreen = state.breakpoints['(min-width: 768px) and (max-width: 1920px)'] ? true : false;

        if (state.breakpoints['(max-width: 1023px)'] && isTabletScreen) {
          this.priceLoaderConfig[0]['theme']['width'] = '3em';
        }
      });

    // Fetches the dayRates
    this.calendarSvc.fetchDayRates$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .pipe(debounceTime(RATE_CALENDAR_SETTINGS.DEFAULT_DEBOUNCE_TIME))
      .subscribe((options) => {
        this.fetchDayRates(options);

        // Refresh the stay day rates
        if (options.refreshRates) {
          this.refreshStayDayRates();
        }
      });

    this.calendarSvc.updateStayDates
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((options) => options !== null),
      )
      .subscribe((options) => {
        const { fromDate, toDate, los, source } = options;
        const hasSource = source && source !== null;

        if (fromDate && !toDate && !los) {
          this.selectDates(fromDate as NgbDateStruct);
        } else if (fromDate && toDate && !los) {
          this.selectDates(fromDate as NgbDateStruct, toDate as NgbDateStruct, undefined, hasSource ? source : null);
        } else if (fromDate && !toDate && los) {
          this.selectDates(fromDate as NgbDateStruct, undefined, los as number);
        }
        this.calendarSvc.updateStayDates.next(null);
      });

    this.calendarSvc.showNoAvailabilityMessage$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((showNoAvailabilityMessage) => {
        if (showNoAvailabilityMessage) {
          this.setErrorMessage('', [], 'NO-AVAILABILITY');
        }
      });

    this.layoutSvc.onPropertyInfoLoaded$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      setTimeout(() => {
        // Sets the minDate based on the isSameDayCheckInAllowed
        this.calendarSvc.setMinDate();
        this.calendarSvc.setMaxDate();

        // Update the dates
        const guestSummary = this.storeSvc.appStore.guestSummary();
        if (guestSummary) {
          const fromDate = DateTime.fromFormat(guestSummary.checkindate, 'yyyy-MM-dd');
          const los = guestSummary.los;

          if (fromDate.isValid) {
            const fromDateNgbDate: NgbDateStruct = {
              year: fromDate.year,
              month: fromDate.month,
              day: fromDate.day,
            };
            this.selectDates(fromDateNgbDate, undefined, los);
          }
        }
      }, 0);
    });

    this.layoutSvc.onTranslationsUpdated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.reRenderCalendar();
    });

    this.calendarSvc.checkinSummaryInitialized$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const checkInDateFromStore = this.storeSvc.appStore.guestSummary()?.checkindate;
      const losFromStore = this.storeSvc.appStore.guestSummary()?.los;
      const fromDateLuxon = DateTime.fromFormat(checkInDateFromStore, 'yyyy-MM-dd');
      const fromDateNgb = {
        year: fromDateLuxon.year,
        month: fromDateLuxon.month,
        day: fromDateLuxon.day,
      };

      this.selectDates(fromDateNgb, undefined, losFromStore);
      this.resetRt4Datalayer();
    });

    this.calendarSvc.checkinSummaryDateInputUpdated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((options) => {
      const { date, source } = options;
      this.onDateSelection(date as NgbDate, source);
    });

    // Observe the calendar container width changes to adjust the number of months displayed on the calendar
    afterNextRender(() => {
      const el = this.calendarContainerRef()?.nativeElement;
      if (!el) return;

      this.calendarContainerObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          this.calendarContainerWidth.set(entry.contentRect.width);
        }
      });

      this.calendarContainerObserver.observe(el);
    });

    // Effect to update the number of months to display based on the calendar container width
    effect(() => {
      const calendarContainerRefWidth = this.calendarContainerRef()?.nativeElement?.clientWidth;
      const calendarContainerWidth = this.calendarContainerWidth();
      const isMobile = untracked(() => this.isMobile());
      const isCalendarSidepanel = untracked(() => this.isCalendarSidepanel());
      const fallBackMonthsToDisplay = isMobile || isCalendarSidepanel ? 1 : 2;

      // Use the calendar container width if available, otherwise use the native element width for initial render
      const widthToUse = calendarContainerWidth ? calendarContainerWidth : calendarContainerRefWidth;
      const noOfMonthsToDisplay = widthToUse && widthToUse < 940 ? 1 : fallBackMonthsToDisplay;

      this.noOfMonthsToDisplay.set(noOfMonthsToDisplay);
    });
  }

  public isHovered(date: NgbDate) {
    return this.hoveredDate && this.hoveredDate.equals(date);
  }

  public isInRange(date: NgbDate) {
    return this.storeSvc.appStore.isDayUseRoomsEnabled()
      ? false
      : (this.fromDate && this.toDate && date.after(this.fromDate) && date.before(this.toDate)) ||
          (this.fromDate && !this.toDate && date.after(this.fromDate) && date.before(this.hoveredDate)) ||
          (this.fromDate && !this.toDate && date.before(this.fromDate) && date.after(this.hoveredDate));
  }

  public isStartRange(date: NgbDate) {
    return this.fromDate && date.equals(this.fromDate);
  }

  public isEndRange(date: NgbDate) {
    return this.storeSvc.appStore.isDayUseRoomsEnabled()
      ? false
      : (this.toDate && date.equals(this.toDate)) || (this.fromDate && !this.toDate && date.equals(this.hoveredDate));
  }

  public isLoading(date: NgbDate) {
    return (
      this.storeSvc.userStore.propertyInfo()?.enableRateCalendar &&
      ((this.isDayRatesLoading() &&
        ((this.monthsLoading.has(date.year + '-' + date.month) &&
          this.monthsLoading.get(date.year + '-' + date.month)) ||
          (this.monthsLoading.has(date.year + '-' + date.month) &&
            this.loadedMonths.has(date.year + '-' + date.month)))) ||
        (this.isStayDayRatesLoading() &&
          this.calendarSvc.stayDayRatesLoading.get(date.year + '-' + date.month + '-' + date.day))) &&
      (date.equals(this.minDate()) || date.after(this.minDate()))
    );
  }
  // Changes the month(previous/next)
  public onMonthChange() {
    if (this.ngbDatepickerRef()?.state) {
      const monthsToLoad = this.ngbDatepickerRef()?.state.months.filter(
        (m) => !this.loadedMonths.has(`${m.year}-${m.month}`),
      );
      if (monthsToLoad && monthsToLoad.length > 0) {
        this.calendarSvc.fetchDayRates$.next({
          isMonthChanged: true,
          monthsToLoad: this.ngbDatepickerRef()?.state.months.filter(
            (m) => !this.loadedMonths.has(`${m.year}-${m.month}`),
          ),
        });
      }
      setTimeout(() => {
        (document.querySelectorAll('.ngb-dp-day') as NodeList).forEach((item) => {
          (item as HTMLElement)?.setAttribute('tabindex', '-1');
        });
      }, 1000);
    }
  }

  public resetRt4Datalayer() {
    if (this.routerSvc.isSearchPage) {
      this.commonSvc.setDatalayer({
        error_type: '',
        error_code: '',
        error_description: '',
      });
    }
  }

  // update the checkIn checkOut Dates  in store
  public onDateSelection(date: NgbDate, source: 'CHECK_IN_INPUT' | 'CHECK_OUT_INPUT' | null = null) {
    const dateStr = `${date.year}-${date.month}-${date.day}`;
    const dateLuxon = DateTime.fromFormat(dateStr, 'yyyy-M-d');
    const dayRateData = this.calendarSvc.getDayRateOf(dateStr);

    let proposedFromDate: NgbDate | null;
    let proposedToDate: NgbDate | null;
    let isSelectionInProgress: boolean;

    if (!this.fromDate && !this.toDate) {
      // First date selection
      proposedFromDate = date;
      proposedToDate = null;
      isSelectionInProgress = true;
    } else if (
      !this.storeSvc.appStore.isDayUseRoomsEnabled() &&
      source !== 'CHECK_OUT_INPUT' &&
      this.fromDate &&
      !this.toDate &&
      date.equals(this.fromDate)
    ) {
      proposedFromDate = date;
      proposedToDate = date;
      isSelectionInProgress = true;

      // Check if user selected the same date as checkout
      this.setErrorMessage('', [], 'SAME-DATE');
      return;
    } else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      // Forward date selection
      proposedFromDate = this.fromDate;
      proposedToDate = date;
      isSelectionInProgress = false;
    } else if (this.fromDate && !this.toDate && date.before(this.fromDate)) {
      // Backward date selection
      proposedFromDate = date;
      proposedToDate = this.fromDate;
      isSelectionInProgress = false;
    } else {
      // Second date selection
      proposedFromDate = date;
      proposedToDate = null;
      isSelectionInProgress = true;
    }

    // Apply day use rooms override
    if (this.storeSvc.appStore.isDayUseRoomsEnabled()) {
      proposedFromDate = date;
      proposedToDate = date;
      isSelectionInProgress = false;
    }

    // Check if day rate data is available and perform validations
    if (dayRateData) {
      // Day rate data exists, perform all validations before updating dates
      const proposedFromDateStr =
        proposedFromDate && `${proposedFromDate.year}-${proposedFromDate.month}-${proposedFromDate.day}`;
      const proposedFromDateRateData = proposedFromDate && this.calendarSvc.getDayRateOf(proposedFromDateStr);
      const proposedToDateStr =
        proposedToDate && `${proposedToDate.year}-${proposedToDate.month}-${proposedToDate.day}`;
      const proposedToDateRateData = proposedToDate && this.calendarSvc.getDayRateOf(proposedToDateStr as string);
      const altPropertyAvailable = dayRateData?.altPropertyAvailable;

      // Validation 1: Check availability (only for fromDate selection, not toDate)
      if (
        !proposedToDate &&
        dayRateData &&
        !dayRateData?.available &&
        !dayRateData?.isClosedToArrival &&
        !dayRateData?.isClosedToDeparture &&
        !altPropertyAvailable
      ) {
        // Not Available - reject selection
        this.setErrorMessage('', [dateLuxon.toFormat('MM/dd/yyyy')], 'NO-CHECK-IN');
        return;
      }

      // Validation 2: Check closed to arrival (initial fromDate selection)
      if (!proposedToDate && dayRateData?.available && dayRateData?.isClosedToArrival && !altPropertyAvailable) {
        // No Check-In - reject selection
        this.setErrorMessage('', [dateLuxon.toFormat('MM/dd/yyyy')], 'NO-CHECK-IN');
        return;
      }

      // Validation 3: Check closed to arrival for backward date selection (new fromDate)
      if (
        proposedFromDate &&
        proposedToDate &&
        date.before(this.fromDate!) &&
        proposedFromDateRateData &&
        proposedFromDateRateData?.available &&
        proposedFromDateRateData?.isClosedToArrival &&
        !altPropertyAvailable
      ) {
        // No Check-In (Backward date selection) - reject selection
        this.setErrorMessage('', [dateLuxon.toFormat('MM/dd/yyyy')], 'NO-CHECK-IN');
        return;
      }

      // Validation 4: Check closed to departure for forward toDate selection
      if (
        proposedFromDate &&
        proposedToDate &&
        date.after(this.fromDate!) &&
        dayRateData?.available &&
        dayRateData?.isClosedToDeparture &&
        !altPropertyAvailable
      ) {
        // No Check-Out (Forward date selection) - reject selection
        this.setErrorMessage('', [dateLuxon.toFormat('MM/dd/yyyy')], 'NO-CHECK-OUT');
        return;
      }

      // Validation 5: Check closed to departure for backward date selection (old fromDate becomes toDate)
      if (
        proposedFromDate &&
        proposedToDate &&
        date.before(this.fromDate!) &&
        proposedToDateRateData &&
        proposedToDateRateData?.available &&
        proposedToDateRateData?.isClosedToDeparture &&
        !altPropertyAvailable
      ) {
        // No Check-Out (Backward date selection - checking old fromDate) - reject selection
        const toDateLuxon = DateTime.fromFormat(
          `${proposedToDate.year}-${proposedToDate.month}-${proposedToDate.day}`,
          'yyyy-M-d',
        );
        this.setErrorMessage('', [toDateLuxon.toFormat('MM/dd/yyyy')], 'NO-CHECK-OUT');
        return;
      }

      // Validation 6: Check LOS restrictions
      if (proposedFromDate && proposedToDate && !altPropertyAvailable) {
        const fromLuxon = DateTime.fromFormat(
          `${proposedFromDate.year}-${proposedFromDate.month}-${proposedFromDate.day}`,
          'yyyy-M-d',
        );
        const toLuxon = DateTime.fromFormat(
          `${proposedToDate.year}-${proposedToDate.month}-${proposedToDate.day}`,
          'yyyy-M-d',
        );
        const dayDifference = Math.abs(toLuxon.diff(fromLuxon, 'days').days);

        if (
          !Number.isNaN(dayDifference) &&
          fromLuxon.isValid &&
          toLuxon.isValid &&
          fromLuxon < toLuxon &&
          toLuxon > fromLuxon
        ) {
          // Validation 7: Check minimum LOS
          if (
            proposedFromDateRateData?.minLos &&
            proposedFromDateRateData.minLos > 1 &&
            dayDifference < proposedFromDateRateData.minLos
          ) {
            // Minimum LOS not met - reject selection and show error message
            this.setErrorMessage('', [fromLuxon.toFormat('MM/dd/yyyy'), proposedFromDateRateData.minLos], 'MIN-LOS');
            return;
          } else if (
            proposedFromDateRateData?.maxLos &&
            proposedFromDateRateData.maxLos > 0 &&
            dayDifference > proposedFromDateRateData.maxLos
          ) {
            // Maximum LOS not met - reject selection and show error message
            this.setErrorMessage('', [fromLuxon.toFormat('MM/dd/yyyy'), proposedFromDateRateData.maxLos], 'MAX-LOS');
            return;
          }
        }
      }

      // Clear error if all validations pass
      this.errorMsg.set('');
    }

    // All validations passed or no day rate data - update dates
    if (!this.storeSvc.appStore.isDayUseRoomsEnabled()) {
      this.fromDate = proposedFromDate;
      this.toDate = proposedToDate;
    } else {
      this.fromDate = proposedFromDate;
      this.toDate = proposedFromDate;
    }

    // Update the stay dates in the store
    const newToDate =
      this.storeSvc.appStore.isDayUseRoomsEnabled() && proposedFromDate
        ? this.ngbCalSvc.getNext(proposedFromDate, 'd', 1)
        : proposedToDate;

    this.calendarSvc.stayDates.set({ fromDate: proposedFromDate, toDate: newToDate });

    if (proposedToDate) {
      this.calendarSvc.priorStayDates.set({ fromDate: proposedFromDate, toDate: proposedToDate });
    } else {
      this.calendarSvc.priorStayDates.update((prev) => ({ ...prev, fromDate: proposedFromDate }));
    }

    this.calendarSvc.isStayDateSelectionInProgress.set(isSelectionInProgress);

    // Apply old day rates
    this.calendarSvc.applyOldDayRates();

    // Update the altPropertyAvailable
    this.calendarSvc.isAltPropertyAvailable.set(
      this.calendarSvc.getDayRateOf(this.fromDate?.year + '-' + this.fromDate?.month + '-' + this.fromDate?.day)
        ?.altPropertyAvailable,
    );

    setTimeout(() => {
      // Update the altPropertyAvailable
      this.calendarSvc.isAltPropertyAvailable.set(
        this.calendarSvc.getDayRateOf(this.fromDate?.year + '-' + this.fromDate?.month + '-' + this.fromDate?.day)
          ?.altPropertyAvailable,
      );
    }, 500);

    if (source === 'CHECK_IN_INPUT' && !this.storeSvc.appStore.isDayUseRoomsEnabled()) {
      // Save the check in date input and enable the check out date input
      this.calendarSvc.checkinSummaryDateInputSaved$.next('CHECK_IN_INPUT');
    } else if (!!this.fromDate && !!this.toDate && !this.isMultiProp()) {
      // Save the check out date input and finish the check out date input editing
      this.storeSvc.appStore.isDayUseRoomsEnabled()
        ? this.calendarSvc.checkinSummaryDateInputSaved$.next('CHECK_IN_INPUT')
        : this.calendarSvc.checkinSummaryDateInputSaved$.next('CHECK_OUT_INPUT');

      // Refresh stay day rates if check in and check out dates are selected
      this.refreshStayDayRates();
    }
  }

  // fetch and set the error based on the ratCal API response for day rates
  public setErrorMessage(
    message: string,
    params?: string[],
    type?:
      | 'NO-CHECK-IN'
      | 'NO-CHECK-OUT'
      | 'MIN-LOS'
      | 'MAX-LOS'
      | 'SAME-DATE'
      | 'RECAPTCHA-FAILED'
      | 'NO-AVAILABILITY',
  ) {
    const errorObj = {
      'NO-CHECK-IN': 'tf_1_Calendar_rateCalender_checkinNotAvailable',
      'NO-CHECK-OUT': 'tf_1_Calendar_rateCalender_checkoutNotAvailable',
      'MIN-LOS': 'tf_1_Calendar_rateCalender_minLOS',
      'MAX-LOS': 'tf_1_Calendar_rateCalender_maxLOS',
      'SAME-DATE': 'tf_1_Calendar_rateCalender_checkinCheckoutSame',
      'RECAPTCHA-FAILED': 'tf_4_googleRecaptcha_verificationFailed',
      'NO-AVAILABILITY': !this.calendarSvc.isAltPropertyAvailable() ? 'tf_99_errorCode_4000' : '',
    };

    if (this.storeSvc.appStore.isDayUseRoomsEnabled()) {
      delete (errorObj as any)['MAX-LOS'];
      delete (errorObj as any)['MIN-LOS'];
    }

    const localeObj = this.storeSvc.userStore.localeObj();
    const errorMessage =
      type && errorObj[type] && localeObj?.[errorObj[type]]
        ? this.commonSvc.fillMessage(localeObj[errorObj[type]], params as string[])
        : message;

    if (errorMessage && (!type || (type && errorObj[type]))) {
      this.modalSvc.displayToastMessage({
        desc: errorMessage,
        type: 'error',
      });
      this.commonSvc.setDatalayer({
        error_type: 'red-error',
        error_code: type || '',
        error_description: errorMessage,
      });
    } else {
      this.commonSvc.setDatalayer({
        error_type: '',
        error_code: '',
        error_description: '',
      });
    }
  }

  public selectDates(
    fromDate?: NgbDateStruct,
    toDate?: NgbDateStruct,
    los?: number,
    source: 'CHECK_OUT_INPUT' | null = null,
  ) {
    let newFromDate: NgbDate | null, newToDate: NgbDate | null;
    if (!fromDate && !toDate && !los) {
      newFromDate = this.ngbCalSvc?.getToday();

      if (!this.storeSvc.userStore.propertyInfo()?.allowSameDayCheckIn) {
        newToDate = this.ngbCalSvc?.getNext(newFromDate, 'd', 1);
      } else {
        newFromDate = this.ngbCalSvc?.getNext(newFromDate, 'd', 1);
        newToDate = this.ngbCalSvc?.getNext(newFromDate, 'd', 1);
      }
    } else if (fromDate && los) {
      newFromDate = NgbDate.from(fromDate);
      newToDate = this.ngbCalSvc?.getNext(newFromDate as NgbDate, 'd', los);
    } else {
      newFromDate = NgbDate.from(fromDate);
      newToDate = NgbDate.from(toDate);
    }

    // Navigate to the month having the new from date
    fromDate && this.navigateToDate$.next(NgbDate.from(fromDate) as NgbDate);

    if (!!newFromDate && !!newToDate) {
      this.onDateSelection(newFromDate, source);
      this.onDateSelection(
        this.storeSvc.appStore.isDayUseRoomsEnabled() ? this.ngbCalSvc?.getNext(newToDate, 'd', 1) : newToDate,
        source,
      );
    } else if (newFromDate) {
      this.onDateSelection(newFromDate);
    } else if (newToDate) {
      this.onDateSelection(
        this.storeSvc.appStore.isDayUseRoomsEnabled() ? this.ngbCalSvc?.getNext(newToDate, 'd', 1) : newToDate,
      );
    }
  }

  // retrive day rates for the specific rate plan
  public fetchDayRates(
    options: {
      refreshRates?: boolean;
      isMonthChanged?: boolean;
      monthsToLoad?: NgbDate[];
      attributes?: any;
      fromPromo?: boolean;
    } = {},
  ) {
    let params: any;
    let startDate: DateTime<boolean>;
    let endDate: DateTime<boolean>;

    const tempCheckinsummary = this.storeSvc.appStore.guestSummary() || {};
    const alreadyLoadedMonths: string[] = [...this.loadedMonths];

    if (options.isMonthChanged && options.monthsToLoad) {
      startDate = DateTime.fromFormat(
        `${options.monthsToLoad[0].year}-${options.monthsToLoad[0].month}`,
        'yyyy-M',
      ).startOf('month');
      endDate = startDate.endOf('month');

      if (options.monthsToLoad.length === 2) {
        endDate = DateTime.fromFormat(
          `${options.monthsToLoad[1].year}-${options.monthsToLoad[1].month}`,
          'yyyy-M',
        ).endOf('month');
      }
    } else if (options.refreshRates && !options.fromPromo) {
      startDate = DateTime.fromFormat(alreadyLoadedMonths[0], 'yyyy-M').startOf('month');
      endDate = DateTime.fromFormat(alreadyLoadedMonths[alreadyLoadedMonths.length - 1], 'yyyy-M').endOf('month');

      if (this.storeSvc.userStore.propertyInfo()?.enableRateCalendar) {
        this.calendarSvc.setMonthsLoading();
      }
    } else if (options.fromPromo) {
      startDate = DateTime.fromFormat(tempCheckinsummary.checkindate, 'yyyy-MM-dd');
      endDate = DateTime.fromFormat(tempCheckinsummary.checkoutdate, 'yyyy-MM-dd');
      startDate = DateTime.now();
      endDate = startDate.plus({ days: 1 });
      startDate = DateTime.fromFormat(`${startDate.year}-${startDate.month + 1}`, 'yyyy-M').startOf('month');
      endDate = DateTime.fromFormat(`${endDate.year}-${endDate.month + 2}`, 'yyyy-M').endOf('month');
    } else {
      startDate = this.storeSvc.userStore.propertyInfo()?.allowSameDayCheckIn
        ? DateTime.now()
        : DateTime.now().plus({ days: 1 });
      endDate = startDate.plus({ months: 1 });
    }

    // Updates the startDate to today's date if the startDate is in past
    if (startDate < this.calendarSvc.minDateLuxon()) {
      startDate = this.calendarSvc.minDateLuxon();
    }

    const endMonth: DateTime<boolean> = endDate;
    let startMonth: DateTime<boolean> = startDate;

    while (startMonth < endMonth) {
      this.storeSvc.userStore.propertyInfo()?.enableRateCalendar &&
        this.monthsLoading.set(startMonth.toFormat('yyyy-M'), true);
      startMonth = startMonth.plus({ months: 1 });
    }

    if (this.routerSvc.isPromoPage || this.routerSvc.isSpecialsPage || this.routerSvc.isGuestInfoPage) {
      tempCheckinsummary.rooms = this.calendarSvc.rooms().length;
      tempCheckinsummary.guests = this.calendarSvc.rooms().map((room) => room.guests);
      params = {
        startDate: startDate.toFormat('yyyy-MM-dd'),
        endDate: endDate.toFormat('yyyy-MM-dd'),
        adults: tempCheckinsummary?.guests[0]?.adults,
        children: tempCheckinsummary?.guests[0]?.children?.length,
        rateCode: 'BAR',
        currency: this.storeSvc.userStore.currency()?.code,
      };
    } else {
      params = {
        startDate: startDate.toFormat('yyyy-MM-dd'),
        endDate: endDate.toFormat('yyyy-MM-dd'),
        adults: tempCheckinsummary?.guests?.[0]?.adults,
        children: tempCheckinsummary?.guests?.[0]?.children?.length,
        // rateCode: this.selectedRateCode,
        currency: this.storeSvc.userStore.currency().code,
        propertyCode: this.storeSvc.userStore.propertyInfo()?.singlePropertyPortal
          ? this.storeSvc.userStore.propertyInfo()?.propertyCode
          : this.storeSvc.userStore.propertyInfo()?.defaultDestinationValue,
        // customTimeout: 1000,
      };

      // Add attributes if provided in options or build from selected filters
      if (options?.attributes) {
        params['attributes'] = options.attributes;
      } else {
        const selectedFilters = this.storeSvc.appStore.selectedAttributeFilters();
        if (selectedFilters && Object.keys(selectedFilters).length > 0) {
          const attributesParam = this.buildAttributesParam();
          if (attributesParam) {
            params['attributes'] = attributesParam;
          }
        }
      }

      params['currency'] = this.storeSvc.userStore.currency()?.code;
      params[QUERY_PARAM_ATTRIBUTES.ACCESS_CODE] = this.storeSvc.appStore.specialCodesData()?.accessCode;
      this.storeSvc.appStore.specialCodesData()?.offerCode &&
        (params[QUERY_PARAM_ATTRIBUTES.OFFERCODE] = this.storeSvc.appStore.specialCodesData()?.offerCode);
      params[QUERY_PARAM_ATTRIBUTES.VALIDATE_IATA_API_PARAM] = this.storeSvc.appStore.specialCodesData()?.iataCode;
    }

    params['isSpecial'] = this.routerSvc.isSpecialsPage;
    params['isPromo'] = this.routerSvc.isPromoPage;

    if (this.storeSvc.userStore.propertyInfo()?.enableRateCalendar && !this.isMultiProp()) {
      this.calendarSvc.isDayRatesLoading.set(this.storeSvc.userStore.propertyInfo.propertyType !== 'UD' ? true : false);
      this.calendarSvc.getRoomRates(params);
    }
  }

  /**
   * Build attributes parameter string from selected filters
   * Format: attributeName1:value1,attributeName2:value2,...
   * For multiple values: attributeName:value1;value2;value3
   */
  private buildAttributesParam(): string {
    const selectedFilters: Record<string, string[]> = this.storeSvc.appStore.selectedAttributeFilters();

    if (!selectedFilters || Object.keys(selectedFilters).length === 0) {
      return '';
    }

    const attributePairs: string[] = [];

    Object.entries(selectedFilters).forEach(([filterKey, selectedIds]: [string, string[]]) => {
      if (selectedIds && selectedIds.length > 0) {
        const valuesString = selectedIds.join(';');
        attributePairs.push(`${filterKey}:${valuesString}`);
      }
    });

    return attributePairs.join(',');
  }

  public handleAction(action: 'SELECT_ROOM' | 'VIEW_ALTERNATE_PROPERTIES') {
    if (action === 'SELECT_ROOM') {
      this.handleSelectRoomWithRecaptcha();
    } else if (action === 'VIEW_ALTERNATE_PROPERTIES') {
      this.saveCheckInSummary();
      this.routerSvc.navigateToPathWithQueryParams(URL_PATHS.ALTERNATE_PROPERTY, {}, 'merge');
    }
  }

  private handleSelectRoomWithRecaptcha() {
    if (checkRecaptchaConfig(this.storeSvc.propInfo())) {
      this.recaptchaSvc.executeAndVerifyRecaptcha({
        action: 'calendar',
        onSuccess: () => {
          this.proceedToSelectRoom();
        },
        onError: () => {
          this.setErrorMessage('', [], 'RECAPTCHA-FAILED');
        },
        loadingSignal: this.isSelectRoomInProgress,
      });
    } else {
      this.proceedToSelectRoom();
    }
  }

  private saveCheckInSummary() {
    const ngbFromDate = this.calendarSvc.stayDates().fromDate;
    const ngbToDate = this.calendarSvc.stayDates().toDate;
    const fromDate = DateTime.fromFormat(`${ngbFromDate?.year}-${ngbFromDate?.month}-${ngbFromDate?.day}`, 'yyyy-M-d');
    const toDate =
      this.storeSvc.appStore.isDayUseRoomsEnabled() && !ngbToDate
        ? fromDate.plus({ days: 1 })
        : DateTime.fromFormat(`${ngbToDate?.year}-${ngbToDate?.month}-${ngbToDate?.day}`, 'yyyy-M-d');
    const los = toDate.diff(fromDate, 'days').days;

    this.calendarSvc.updateCheckinSummary(fromDate.toFormat('yyyy-MM-dd'), toDate.toFormat('yyyy-MM-dd'), los);
  }

  private proceedToSelectRoom() {
    this.saveCheckInSummary();

    // Check if the current page is the search page and the access code is present
    // If yes, then update the Group Results by Rateplan filter
    const specialCodesData = this.storeSvc.appStore.specialCodesData();
    const hasAccessCode = specialCodesData?.accessCode?.length > 0 && specialCodesData?.accessCodeApplied;
    if (this.routerSvc.isSearchPage && hasAccessCode) {
      this.storeSvc.updateAppState('groupBy', 'RATEPLANS');
    }

    // Navigate to the rooms page
    setTimeout(() => {
      // Update the selected rooms in the store by removing the rooms that are null
      const selectedRooms = this.storeSvc.appStore.selectedRooms();
      if (!isEmpty(selectedRooms)) {
        const newSelectedRooms = Object.entries(selectedRooms)
          .filter(([argIgnore_Key, value]) => !isNull(value))
          .reduce((acc: Record<string, any>, [key, value]) => {
            acc[key] = value;
            return acc;
          }, {});
        this.storeSvc.updateAppState('selectedRooms', newSelectedRooms);
      }

      // Wait for the selected rooms to be updated in the store
      setTimeout(() => this.routerSvc.navigateToRoomsPage());
    }, 100);
  }

  public handleModalAction(action: 'CANCEL' | 'SAVE') {
    if (action === 'SAVE') {
      this.calendarSvc.saveChangesToStayDates();
    }
    this.closeModalEvent.emit();
  }

  public reRenderCalendar() {
    this.showCalendar.set(false);

    setTimeout(() => {
      this.showCalendar.set(true);
    }, 0);
  }

  public refreshStayDayRates = () => {
    const { fromDate, toDate } = this.calendarSvc.stayDates();
    const parsedFromDateLuxon = DateTime.fromFormat(
      `${fromDate?.year}-${fromDate?.month}-${fromDate?.day}`,
      'yyyy-M-d',
    );
    const parsedToDateLuxon = DateTime.fromFormat(`${toDate?.year}-${toDate?.month}-${toDate?.day}`, 'yyyy-M-d');
    const los = parsedToDateLuxon.diff(parsedFromDateLuxon, 'days').days;

    if (
      parsedFromDateLuxon.isValid &&
      parsedToDateLuxon.isValid &&
      parsedToDateLuxon > parsedFromDateLuxon &&
      los > 0
    ) {
      // Fetch the stay day rates
      this.calendarSvc.getStayDayRates();
    }
  };
}
